import { useCallback, useEffect, useMemo, useState } from 'react';

import { isEqual, isNull } from 'lodash';

import {
  Button,
  Chip,
  Input,
  JsonTree,
  Modal,
  PermissionAccess,
} from '@components/index';
import { UserPermissions } from '@enums/user-permissions';

import { ContentModalActionType, LabelModalData } from './types';

type Props = {
  isOpen: boolean;
  isLoading?: boolean;
  type: ContentModalActionType | null;
  data?: any;
  handleSave: (labelData: LabelModalData) => void;
  handleCancel: () => void;
  customThumbnailUrl?: string;
  isReadonly?: boolean;
};

export const LabelMetadataModal = ({
  isOpen,
  isLoading,
  type,
  data,
  handleSave,
  handleCancel,
  isReadonly = false,
}: Props) => {
  const [currentModalData, setCurrentModalData] = useState({
    name: '',
    metadata: {},
  });

  useEffect(() => {
    if (data) {
      const { name, metadata } = data;

      const { _is_editable, ...customMetadataCopy } = metadata || {};

      setCurrentModalData({ name, metadata: customMetadataCopy });
    }
  }, []);

  const buttonLabels = {
    [ContentModalActionType.CREATE_LABEL]: 'Create label',
    [ContentModalActionType.EDIT_LABEL]: 'Save label',
    [ContentModalActionType.LABEL_METADATA]: 'Save metadata',
    [ContentModalActionType.DELETE_LABEL]: 'Delete label',
  };

  const getButtonLabel = (type: ContentModalActionType | null): string =>
    !isNull(type) ? buttonLabels[type] : 'Save';

  const getHeader = useCallback(
    (type: ContentModalActionType | null, name: string) => {
      switch (type) {
        case ContentModalActionType.CREATE_LABEL:
          return 'Create new label';
        case ContentModalActionType.EDIT_LABEL:
          return 'Edit label';

        case ContentModalActionType.LABEL_METADATA:
          return (
            <div className="flex gap-2">
              <div>Metadata:</div>
              <Chip label={name} showTooltip={true} showRemoveButton={false} />
            </div>
          );
        default:
          return 'Edit';
      }
    },
    [],
  );

  const handleCloseModal = () => {
    setCurrentModalData({ name: '', metadata: {} });
    handleCancel();
  };

  const getMetaDataTitle = useCallback(() => {
    let metaDataTitle = 'Label metadata';
    if (type !== ContentModalActionType.LABEL_METADATA) {
      metaDataTitle += ' (optional)';
    }

    return metaDataTitle;
  }, [type]);

  const handleDataChange = (updatedData: LabelModalData) => {
    setCurrentModalData(prevState => ({
      ...prevState,
      ...updatedData,
    }));
  };

  const checkIfDataIsChanged = useCallback(() => {
    if (!data || !currentModalData) return false;

    const { name, metadata } = data;
    const { _is_editable, ...customMetadataCopy } = metadata || {};

    return !(
      isEqual(name.trim(), currentModalData.name.trim()) &&
      isEqual(customMetadataCopy, currentModalData.metadata || {})
    );
  }, [data, currentModalData]);

  const isSaveDisabled = useMemo(() => {
    const isDataUnchanged = !checkIfDataIsChanged();

    if (type === ContentModalActionType.CREATE_LABEL) {
      return !currentModalData.name.trim() || isLoading;
    }

    if (type === ContentModalActionType.EDIT_LABEL) {
      return !currentModalData.name.trim() || isLoading || isDataUnchanged;
    }

    if (type === ContentModalActionType.LABEL_METADATA) {
      return isLoading || isDataUnchanged;
    }

    return true;
  }, [type, currentModalData.name, isLoading, checkIfDataIsChanged]);

  return (
    <Modal isOpen={isOpen} onClose={handleCloseModal}>
      <div className="p-8 w-[600px] flex flex-col gap-4 text-text-primary">
        <div className="text-base font-bold">
          {getHeader(type, currentModalData?.name || '')}
        </div>

        {(type === ContentModalActionType.EDIT_LABEL ||
          type === ContentModalActionType.CREATE_LABEL) && (
          <div className="flex flex-col gap-4">
            <div>
              <div className="text-sm font-semibold mb-1">Label name</div>
              <Input
                containerClass="w-full"
                placeholder="Write label name"
                value={currentModalData?.name || ''}
                onChange={e => handleDataChange({ name: e.target.value })}
              />
            </div>
          </div>
        )}
        <PermissionAccess
          permission={UserPermissions.MANAGE_BRAND_LIBRARY}
          AllowedContent={
            <>
              <div>
                {(type === ContentModalActionType.EDIT_LABEL ||
                  type === ContentModalActionType.CREATE_LABEL) && (
                  <div className="text-sm font-semibold mb-1">
                    {getMetaDataTitle()}
                  </div>
                )}
                <JsonTree
                  data={currentModalData.metadata || {}}
                  readOnly={isLoading || isReadonly}
                  onUpdate={
                    isReadonly
                      ? undefined
                      : metadata => handleDataChange({ metadata })
                  }
                />
              </div>

              {!isReadonly && (
                <div className="sticky bottom-0 left-0 flex justify-between">
                  <Button
                    variant="outlined"
                    label="Cancel"
                    onClick={handleCloseModal}
                    classOverride="!w-[260px]"
                  />
                  <Button
                    variant="contained"
                    onClick={() =>
                      handleSave({
                        ...currentModalData,
                        name: currentModalData.name.trim(),
                        metadata: currentModalData.metadata,
                      })
                    }
                    isLoading={isLoading}
                    isDisabled={isSaveDisabled}
                    label={getButtonLabel(type)}
                    classOverride="!w-[260px]"
                  />
                </div>
              )}
            </>
          }
          DeniedContent={
            <JsonTree data={currentModalData.metadata || {}} readOnly={true} />
          }
        />
      </div>
    </Modal>
  );
};
